/**
 * Per-domain failover designations (ADR 0029).
 *
 * Which node and tenant domain each discovered domain fails over to, and
 * the advisories that say when a designation has gone stale or the estate
 * has become lopsided.
 */

import { apiFetch } from "./client";

/** One discovered domain and its designated backup, if any. */
export interface FailoverDomainRow {
  source_node_id: number;
  source_node: string;
  source_domain: string;
  backup_node_id: number | null;
  backup_node: string | null;
  backup_domain: string | null;
  note: string | null;
  /** Set when the source domain is no longer in the inventory. */
  orphaned?: boolean;
}

/** Per-node backup load. */
export interface BalanceRow {
  node_id: number;
  node: string;
  backups: number;
}

/** A stale designation or a lopsided estate — advice, never applied. */
export interface FailoverAdvisory {
  kind: "designation-drift" | "backup-imbalance";
  message: string;
  source_node_id?: number;
  source_domain?: string;
  observed_node_id?: number;
  observed_domain?: string;
  node_id?: number;
  share?: number;
}

export interface FailoverOverview {
  items: FailoverDomainRow[];
  total: number;
  balance: { nodes: BalanceRow[]; total: number; advisories: FailoverAdvisory[] };
  advisories: FailoverAdvisory[];
}

export function getFailoverDomains(): Promise<FailoverOverview> {
  return apiFetch("/api/instance/failover/domains");
}

/** Node + domain combinations that may be chosen as a backup target. */
export interface FailoverCandidate {
  node_id: number;
  node: string;
  domains: string[];
}

export function getFailoverCandidates(): Promise<{ items: FailoverCandidate[] }> {
  return apiFetch("/api/instance/failover/candidates");
}

export interface DesignationIn {
  source_node_id: number;
  source_domain: string;
  backup_node_id: number;
  backup_domain: string;
  note?: string | null;
}

export function setFailoverDesignation(body: DesignationIn): Promise<FailoverDomainRow> {
  return apiFetch("/api/instance/failover/domains", { method: "PUT", body });
}

export function clearFailoverDesignation(
  sourceNodeId: number,
  sourceDomain: string,
): Promise<void> {
  const qs = `source_node_id=${sourceNodeId}&source_domain=${encodeURIComponent(sourceDomain)}`;
  return apiFetch(`/api/instance/failover/domains?${qs}`, { method: "DELETE" });
}
